import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import bankService from "../../../services/bank.service";
import { useAuthContext } from "../../../context/AuthContext";
import PortalSignInLayout from "../../../components/auth/PortalSignInLayout";
import "../../../styles/auth.css";

export default function BankLogin() {
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const [form, setForm] = useState({
    roleType: "admin",
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await bankService.login({
        email: form.email,
        password: form.password,
      });
      const { token, user } = res.data.data;

      if (!token || !user) {
        setError("Login failed");
        return;
      }

      if (user.role !== "BANK_ADMIN" && user.role !== "BANK_PERSON") {
        setError("This portal is only for bank / NBFC admins and bank / NBFC users.");
        return;
      }

      if (form.roleType === "admin" && user.role !== "BANK_ADMIN") {
        setError("Please choose Bank / NBFC Admin and sign in with your admin credentials.");
        return;
      }

      if (form.roleType === "user" && user.role !== "BANK_PERSON") {
        setError("Please choose Bank / NBFC User and sign in with your bank / NBFC person credentials.");
        return;
      }

      login({ token, user });
      navigate("/bank/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalSignInLayout
      breadcrumb={[{ label: "Bank / NBFC Login" }]}
      title="Bank / NBFC portal"
      subtitle="Sign in with your email and password. Your bank / NBFC is applied automatically from your account. Bank / NBFC admins manage users and uploads; bank / NBFC users work on assigned records."
      perks={[
        "Your bank / NBFC is identified from your credentials",
        "Separate admin and bank / NBFC user access",
        "Same credentials as provided by your bank / NBFC admin",
      ]}
      cardTitle="SIGN-IN USING :"
    >
      <form onSubmit={handleSubmit} className="portal-signin-form">
        <div className="portal-signin-role-group" role="radiogroup" aria-label="Sign in as">
          <label>
            <input
              type="radio"
              name="roleType"
              value="admin"
              checked={form.roleType === "admin"}
              onChange={handleChange}
            />
            BANK ADMIN
          </label>
          <label>
            <input
              type="radio"
              name="roleType"
              value="user"
              checked={form.roleType === "user"}
              onChange={handleChange}
            />
            BANK USER
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Enter your email"
            required
            autoComplete="email"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            placeholder="Enter your password"
            required
            autoComplete="current-password"
          />
        </div>

        {error && <p className="error-text">{error}</p>}

        <button type="submit" className="portal-signin-submit" disabled={loading}>
          {loading ? "Signing in…" : "Login"}
        </button>

        <div className="portal-signin-links">
          <p>
            New bank / NBFC? <Link to="/bank/register">Register here</Link>
          </p>
          <p>
            <Link to="/bank/forgot-password">Forgot password?</Link>
          </p>
        </div>
      </form>
    </PortalSignInLayout>
  );
}
